import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AppInfoService } from 'core/app-info/app-info.service';
import { UtilsService } from 'core/utils/utils.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { QuoteAttributes } from '../entities/quote-attributes.interface';
import { QuoteDetailsService } from '../services/quote-details.service';
import { PaymentDetailsService } from '../services/payment-details.service';

@Component({
  selector: 'app-quote-options',
  templateUrl: './quote-options.component.html',
  styleUrls: ['./quote-options.component.scss'],
  standalone: false
})
export class QuoteOptionsComponent implements OnInit, OnDestroy {
  @Input() quoteId: string;
  @Input() canEdit: boolean;
  @Input() countryCode: string;

  appStrings: AppLocalization;
  isMobile: boolean;
  quoteOptionsForm: FormGroup;
  customerTypes: { value: string, label: string }[] = [];
  customerTypesKeys: string[] = [];
  hideCustomerType: boolean;
  templateValues: any = {};

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private appInfoService: AppInfoService,
    private quoteDetailsService: QuoteDetailsService,
    private paymentDetailsService: PaymentDetailsService,
    private utilsService: UtilsService,
    private deviceService: DeviceDetectorService,
    private cdRef: ChangeDetectorRef
  ) { }

  get effectiveCountryCode(): string {
    return this.countryCode || this.appInfoService.userData.UserInfo.CountryCode;
  }

  get numberOfSelectedCheckboxes(): number {
    if (!this.quoteOptionsForm) {
      return 0;
    }
    const formValue = this.quoteOptionsForm.value;
    return Object.keys(formValue)
      .filter(key => key !== 'customerType' && key !== 'hideInactiveCustomers' && formValue[key] === true)
      .length;
  }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.appStrings = this.appInfoService.appStrings;

    this.initForm();
    this.loadStaticValues();

    this.subs.add(this.paymentDetailsService.userAttributes$.subscribe(attributes => {
      if (this.quoteOptionsForm && attributes) {
        this.quoteOptionsForm.patchValue(attributes);
        if (attributes.customerType !== null && attributes.customerType !== undefined) {
          this.quoteOptionsForm.controls.customerType.setValue(attributes.customerType.toString());
        }
        this.validateCustomerType();
        this.cdRef.detectChanges();
      }
    }));

    this.subs.add(this.quoteDetailsService.quoteIsSaving$.subscribe(isSaving => {
      if (this.quoteOptionsForm) {
        if (isSaving) {
          this.quoteOptionsForm.disable();
        } else if (this.canEdit) {
          this.quoteOptionsForm.enable();
        }
      }
    }));

    this.subs.add(this.quoteDetailsService.isCompleted$.subscribe(isCompleted => {
      this.canEdit = !isCompleted;
      if (this.quoteOptionsForm) {
        if (this.canEdit) {
          this.quoteOptionsForm.enable();
        } else {
          this.quoteOptionsForm.disable();
        }
      }
    }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private initForm() {
    this.quoteOptionsForm = this.fb.group({
      customerType: null,
      lineNumber: false,
      oemSku: false,
      commCode: false,
      countryOfOrigin: false,
      sinNumber: false,
      listPrice: false,
      netPrice: false,
      grandTotal: false,
      lineNotes: false,
      quoteNotes: false,
      vat: false,
      lineWeight: false,
      estimatedShipDate: false,
      hideInactiveCustomers: false
    });

    if (this.appInfoService.userData.UserInfo.CountryCode === 'DE') {
      this.hideCustomerType = true;
    } else {
      this.hideCustomerType = false;
    }
  }

  private loadStaticValues() {
    this.quoteDetailsService.getStaticValues(([templateValues]) => {
      this.templateValues = templateValues;
      const customerTypes = templateValues.CustomerType;
      this.customerTypes = this.utilsService.mapKeyValueOptions(customerTypes);
      this.customerTypesKeys = Object.keys(customerTypes);
      this.loadUserAttributes();
    });
  }

  private loadUserAttributes() {
    this.paymentDetailsService.getUserAttributes((attributes) => {
      this.quoteOptionsForm.patchValue(attributes);
      if (attributes.customerType !== null && attributes.customerType !== undefined) {
        this.quoteOptionsForm.controls.customerType.setValue(attributes.customerType.toString());
      }
      this.validateCustomerType();
      if (!this.canEdit) {
        this.quoteOptionsForm.disable();
      }
      this.cdRef.detectChanges();
    });
  }

  private validateCustomerType() {
    const customerType = this.quoteOptionsForm.controls.customerType.value;
    if (!customerType || !this.customerTypesKeys.includes(customerType.toString())) {
      if (this.customerTypesKeys.length > 0) {
        this.quoteOptionsForm.controls.customerType.setValue(this.customerTypesKeys[0]);
      }
    }
  }

  getQuoteAttributes(): QuoteAttributes {
    return {
      customerType: this.getCustomerType(),
      lineNumber: this.quoteOptionsForm.value.lineNumber,
      oemSku: this.quoteOptionsForm.value.oemSku,
      commCode: this.quoteOptionsForm.value.commCode,
      countryOfOrigin: this.quoteOptionsForm.value.countryOfOrigin,
      sinNumber: this.quoteOptionsForm.value.sinNumber,
      listPrice: this.quoteOptionsForm.value.listPrice,
      netPrice: this.quoteOptionsForm.value.netPrice,
      grandTotal: this.quoteOptionsForm.value.grandTotal,
      lineNotes: this.quoteOptionsForm.value.lineNotes,
      quoteNotes: this.quoteOptionsForm.value.quoteNotes,
      vat: this.quoteOptionsForm.value.vat,
      lineWeight: this.quoteOptionsForm.value.lineWeight,
      estimatedShipDate: this.quoteOptionsForm.value.estimatedShipDate,
      hideInactiveCustomers: this.quoteOptionsForm.value.hideInactiveCustomers
    };
  }

  isCustomerTypeValid(): boolean {
    return !!this.quoteOptionsForm?.controls?.customerType?.value;
  }

  hasChanges(): boolean {
    return this.quoteOptionsForm?.dirty || false;
  }

  markAsPristine() {
    this.quoteOptionsForm.markAsPristine();
  }

  private getCustomerType(): number {
    return Number(this.quoteOptionsForm.controls.customerType.value || 0);
  }
}
